import { useState } from "react";
import { toast } from "sonner";
import { Copy, Share2, Check } from "lucide-react";
import { track } from "../lib/api";

/* Reusable share-actions row — sits beneath a result, above the disclaimer.
   Uses navigator.share on capable devices (iOS/Android + some desktop
   browsers) and falls back to clipboard-write. Shares plain text only —
   no PII, no partner details, no URL params. */
const ShareActions = ({
  shareText,
  shareTitle,
  copyEvent,
  shareEvent,
  testIdPrefix,
  copyLabel = "Copy result",
  note = "Shares text only — no personal data or partner details.",
}) => {
  const [copied, setCopied] = useState(false);
  const nativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      toast.success("Result copied");
      track(copyEvent, { has_native_share: nativeShare });
      setTimeout(() => setCopied(false), 2000);
      return true;
    } catch {
      toast.error("Couldn't copy — please long-press the text above to copy manually.");
      return false;
    }
  };

  const doShare = async () => {
    if (!nativeShare) {
      // No native share (e.g. desktop Firefox) — copy, but still record share intent.
      const ok = await doCopy();
      if (ok) track(shareEvent, { method: "clipboard_fallback" });
      return;
    }
    try {
      await navigator.share({ title: shareTitle, text: shareText });
      track(shareEvent, { method: "native" });
    } catch (err) {
      // navigator.share throws AbortError when the user cancels — don't count as a share.
      if (err && err.name === "AbortError") return;
      // Any other failure -> fall back to clipboard, recorded as a share (fallback).
      const ok = await doCopy();
      if (ok) track(shareEvent, { method: "clipboard_fallback" });
    }
  };

  return (
    <div
      className="mt-6 flex flex-wrap gap-2.5 items-center border-t border-[#DCE7E3] pt-5"
      data-testid={`${testIdPrefix}-share-actions`}
      aria-label="Share this result"
    >
      <button
        type="button"
        onClick={doCopy}
        data-testid={`${testIdPrefix}-copy-result`}
        className="inline-flex items-center gap-2 text-sm font-medium text-[#0F766E] bg-[#0F766E]/8 hover:bg-[#0F766E]/15 border border-[#0F766E]/25 hover:border-[#0F766E]/50 rounded-full px-4 py-2 transition-colors"
      >
        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        {copied ? "Copied" : copyLabel}
      </button>
      <button
        type="button"
        onClick={doShare}
        data-testid={`${testIdPrefix}-share-result`}
        className="inline-flex items-center gap-2 text-sm font-medium text-[#0F766E] bg-[#0F766E]/8 hover:bg-[#0F766E]/15 border border-[#0F766E]/25 hover:border-[#0F766E]/50 rounded-full px-4 py-2 transition-colors"
      >
        <Share2 className="w-4 h-4" />
        Share result
      </button>
      <span className="text-xs text-[#94A3B8] ml-1">{note}</span>
    </div>
  );
};

export default ShareActions;
